import {
  DebouncedSearchInput,
  Modal,
  ModalActionButtonProps,
  useRunWithFlag,
} from "@msys/ui";
import { Stack, Typography } from "@mui/material";
import { SimpleTreeView } from "@mui/x-tree-view";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { CategoryTreeItems } from "./CategoryTreeItems.js";
import { buildCategoryTree, filterCategories } from "./helpers.js";
import type { CategoryFragment } from "./useCategories.generated.js";
import { useCategories } from "./useCategories.js";
import { useCategoriesExpandedItems } from "./useCategoriesExpandedItems.js";

interface Props {
  id?: string;
  title?: string;
  handleClose: () => void;
  handleSelectCategories: (
    categories: CategoryFragment[],
    handleClose: () => void
  ) => Promise<void> | void;
  handleClearCategories?: (handleClose: () => void) => Promise<void> | void;
  onCategoryClick?: (category: CategoryFragment) => void;
  excludeCategoryIds?: string[];
  initialSelectedCategoryIds?: string[];
}

export const CategoriesSearchModal = ({
  id,
  title,
  handleClose,
  handleSelectCategories,
  handleClearCategories,
  onCategoryClick,
  excludeCategoryIds,
  initialSelectedCategoryIds,
}: Props) => {
  const { t } = useTranslate(["Categories", "Global"]);

  const [selectedCategoryIds, setSelectedCategoryIds] = React.useState<
    string[]
  >(initialSelectedCategoryIds ?? []);

  const [processingSelect, runSelect] = useRunWithFlag();
  const [processingClear, runClear] = useRunWithFlag();
  const processing = processingSelect || processingClear;

  const { getCategories, loading } = useCategories();
  const categories = React.useMemo(
    () =>
      getCategories({
        addParentsWhenNotIncluded: false,
        removeChildrenIfIncluded: false,
      }),
    [getCategories]
  );

  // Remove selected category IDs that are not in the loaded categories
  React.useEffect(() => {
    if (categories.length > 0) {
      const categoryIds = new Set(categories.map(c => c.id));
      const validSelectedIds = selectedCategoryIds.filter(id =>
        categoryIds.has(id)
      );
      if (validSelectedIds.length !== selectedCategoryIds.length) {
        setSelectedCategoryIds(validSelectedIds);
      }
    }
  }, [categories, selectedCategoryIds]);

  return (
    <Modal
      id={id}
      maxWidth="sm"
      title={title ?? t("Select categories", { ns: "Categories" })}
      actionButtons={
        [
          {
            label: t("Cancel", { ns: "Global" }),
            handleClick: handleClose,
            buttonProps: { variant: "text", disabled: processing },
          },
          ...(handleClearCategories
            ? ([
                {
                  label: t("Remove categories", {
                    ns: "Categories",
                  }),
                  handleClick: async () => {
                    await runClear(async () => {
                      await handleClearCategories(handleClose);
                    });
                  },
                  buttonProps: {
                    loading: processingClear,
                    variant: "outlined",
                    disabled: processing,
                  },
                },
              ] as ModalActionButtonProps[])
            : []),
          {
            label: t("Select", { ns: "Global" }),
            handleClick: async () => {
              if (selectedCategoryIds.length === 0)
                throw new Error("Categories missing");
              await runSelect(async () => {
                const selectedCategories = categories.filter(c =>
                  selectedCategoryIds.includes(c.id)
                );
                if (selectedCategories.length === 0)
                  throw new Error("Categories not found");
                await handleSelectCategories(selectedCategories, handleClose);
              });
            },
            buttonProps: {
              loading: processingSelect,
              disabled: selectedCategoryIds.length === 0 || processing,
            },
          },
        ] as ModalActionButtonProps[]
      }
      headerActions={
        selectedCategoryIds.length > 0 ? (
          <Typography variant="body2">
            {t("{selectedCount} selected", {
              ns: "Global",
              selectedCount: selectedCategoryIds.length,
            })}
          </Typography>
        ) : undefined
      }
      handleClose={handleClose}
      isLoading={loading}
    >
      <CategoriesSearchList
        selectedCategoryIds={selectedCategoryIds}
        setSelectedCategoryIds={setSelectedCategoryIds}
        onCategoryClick={onCategoryClick}
        excludeCategoryIds={excludeCategoryIds}
        categories={categories}
      />
    </Modal>
  );
};

export const CategoriesSearchList = ({
  selectedCategoryIds,
  setSelectedCategoryIds,
  onCategoryClick,
  excludeCategoryIds,
  categories,
}: {
  selectedCategoryIds: string[];
  setSelectedCategoryIds: React.Dispatch<React.SetStateAction<string[]>>;
  onCategoryClick?: (category: CategoryFragment) => void;
  excludeCategoryIds?: string[];
  categories: CategoryFragment[];
}) => {
  const { t } = useTranslate(["Categories", "Global"]);

  const [searchTerm, setSearchTerm] = React.useState<string>("");

  const filteredCategories = React.useMemo(
    () => filterCategories(categories, searchTerm),
    [categories, searchTerm]
  );

  const { categoriesTree, categoriesMap } = React.useMemo(
    () => buildCategoryTree(filteredCategories),
    [filteredCategories]
  );

  const [expandedItemIds, setExpandedItemIds] = useCategoriesExpandedItems(
    categoriesMap,
    selectedCategoryIds
  );

  // Helper function to get all descendant IDs of a category
  const getAllDescendantIds = React.useCallback(
    (categoryId: string): string[] => {
      const category = categoriesMap.get(categoryId);
      if (!category?.children || category.children.length === 0) {
        return [];
      }

      const descendants: string[] = [];
      for (const child of category.children) {
        descendants.push(child.id);
        descendants.push(...getAllDescendantIds(child.id));
      }
      return descendants;
    },
    [categoriesMap]
  );

  // Handle selection changes with descendants
  const handleSelectionChange = React.useCallback(
    (_e: React.SyntheticEvent | null, newItemIds: string[]) => {
      const previousIds = new Set(selectedCategoryIds);
      const newIds = new Set(newItemIds);

      // Find what changed (added or removed)
      const addedIds = newItemIds.filter(id => !previousIds.has(id));
      const removedIds = selectedCategoryIds.filter(id => !newIds.has(id));

      const addedIdsSet = new Set(addedIds);
      const removedIdsSet = new Set(removedIds);

      let finalIds = new Set(newItemIds);

      // Handle added categories - add all descendants only when no descendants are added in the same operation
      for (const addedId of addedIds) {
        const descendants = getAllDescendantIds(addedId);
        // Check if any descendants are being added in this same operation
        const hasAddedDescendants = descendants.some(id => addedIdsSet.has(id));

        if (!hasAddedDescendants) {
          // No descendants being added in this operation, add all descendants
          descendants.forEach(id => finalIds.add(id));
        }
      }

      // Handle removed categories - remove all descendants only when no descendants are removed in the same operation
      for (const removedId of removedIds) {
        const descendants = getAllDescendantIds(removedId);
        // Check if any descendants are being removed in this same operation
        const hasRemovedDescendants = descendants.some(id =>
          removedIdsSet.has(id)
        );

        if (!hasRemovedDescendants) {
          // No descendants being removed in this operation, remove all descendants
          descendants.forEach(id => finalIds.delete(id));
        }
        finalIds.delete(removedId);
      }

      // Excluded categories can never be selected, not even as descendants
      // of a ticked parent.
      excludeCategoryIds?.forEach(id => finalIds.delete(id));

      setSelectedCategoryIds(Array.from(finalIds));
    },
    [
      selectedCategoryIds,
      getAllDescendantIds,
      setSelectedCategoryIds,
      excludeCategoryIds,
    ]
  );

  if (!categories.length) return null;

  return (
    <Stack direction="column" spacing={2}>
      <DebouncedSearchInput
        defaultValue={searchTerm}
        placeholder={t("Search", { ns: "Global" })}
        onChangeSearchTerm={setSearchTerm}
        autoFocus
      />
      <SimpleTreeView
        expansionTrigger="iconContainer"
        multiSelect={true}
        checkboxSelection={true}
        selectedItems={selectedCategoryIds}
        onSelectedItemsChange={handleSelectionChange}
        selectionPropagation={{ descendants: true, parents: true }}
        expandedItems={expandedItemIds}
        onItemExpansionToggle={(_e, itemId, isExpanded) => {
          if (isExpanded) {
            setExpandedItemIds(ids => [...ids, itemId]);
          } else {
            setExpandedItemIds(ids => ids.filter(id => id !== itemId));
          }
        }}
      >
        <CategoryTreeItems
          categories={categoriesTree}
          onCategoryClick={onCategoryClick}
          excludeCategoryIds={excludeCategoryIds}
        />
      </SimpleTreeView>
    </Stack>
  );
};
